<template>
  <div class="contractor-layout-root">
    <VaLayout :top="{ fixed: true }" :left="{ fixed: true }">
      <template #top>
        <!-- Impersonation banner -->
        <div v-if="contractorAuth.isImpersonating" class="impersonation-banner">
          <VaIcon name="manage_accounts" size="20px" class="impersonation-banner__icon" />
          <span
            >You are viewing as <strong>{{ contractor?.company_name }}</strong></span
          >
          <VaButton color="warning" size="small" @click="exitImpersonation"> Exit impersonation </VaButton>
        </div>

        <header class="contractor-layout__header">
          <div class="contractor-layout__header-start">
            <button
              class="contractor-layout__icon-button contractor-layout__menu-toggle"
              type="button"
              :aria-label="sidebarMinimized ? 'Expand menu' : 'Collapse menu'"
              @click="sidebarMinimized = !sidebarMinimized"
            >
              <VaIcon name="menu" />
            </button>
            <RouterLink :to="{ name: 'contractor-dashboard' }" class="contractor-layout__logo">
              <img v-if="clientLogoUrl" :src="clientLogoUrl" alt="Client logo" class="contractor-layout__logo-image" />
              <VuesticLogo v-else :height="24" />
            </RouterLink>
          </div>
          <div class="contractor-layout__actions">
            <span v-if="contractor" class="contractor-layout__company">{{ contractor.company_name }}</span>
            <div class="contractor-layout__icon-row">
              <button class="contractor-layout__icon-button" type="button" @click="goPreferences">
                <VaIcon name="settings" />
              </button>
              <button
                v-if="vehicleFeaturesEnabled"
                class="contractor-layout__icon-button contractor-layout__icon-button--notifications"
                type="button"
                @click="notificationsOpen = true"
              >
                <VaIcon name="notifications" />
                <span v-if="unread > 0" class="contractor-layout__badge">{{ unread }}</span>
              </button>
              <button class="contractor-layout__avatar-button" type="button">
                <img src="https://picsum.photos/100/100" alt="User avatar" class="contractor-layout__avatar" />
              </button>
            </div>
            <VaButton :color="contractorAuth.isImpersonating ? 'warning' : 'danger'" size="small" @click="logout">
              {{ contractorAuth.isImpersonating ? 'Exit impersonation' : 'Logout' }}
            </VaButton>
          </div>
        </header>
      </template>

      <template #left>
        <ClientPortalSidebar v-model:minimized="sidebarMinimized" :mobile="false" />
      </template>

      <template #content>
        <main class="contractor-main" :class="{ 'contractor-main--no-padding': isLiveTracker }">
          <RouterView />
        </main>
      </template>
    </VaLayout>

    <VaModal
      v-model="notificationsOpen"
      title="Notifications"
      close-button
      ok-text="Mark all read"
      @ok="handleMarkAllRead"
    >
      <div class="mb-3 flex items-center gap-2 overflow-auto">
        <button
          v-for="tab in violationTabs"
          :key="tab.id"
          type="button"
          class="rounded-md border px-3 py-1 text-xs font-semibold whitespace-nowrap"
          :class="
            activeViolationTab === tab.id
              ? 'bg-[var(--va-primary)] text-white border-[var(--va-primary)]'
              : 'bg-transparent text-secondary border-[var(--va-background-border)]'
          "
          @click="activeViolationTab = tab.id"
        >
          {{ tab.label }} ({{ tab.count }})
        </button>
      </div>

      <div class="mb-3 flex items-center gap-2">
        <VaButton preset="secondary" size="small" @click="handleClearCurrentTab">Clear current tab</VaButton>
        <VaButton preset="secondary" color="danger" size="small" @click="handleClearAll">Clear all</VaButton>
      </div>

      <div v-if="filteredNotifications.length === 0" class="py-6 text-center text-secondary text-sm">
        No notifications in this tab.
      </div>
      <div v-else class="space-y-2 max-h-[65vh] overflow-auto pr-1">
        <div
          v-for="n in filteredNotifications"
          :key="n.id"
          class="rounded-md border p-3"
          :class="n.readAt ? 'opacity-80 border-[var(--va-background-border)]' : 'border-[var(--va-primary)]'"
          @click="openNotification(n)"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="font-semibold text-sm truncate">{{ n.title }}</div>
              <div class="text-xs text-secondary mt-1">{{ n.message }}</div>
              <div v-if="n.type === 'violation'" class="mt-2 text-[11px] text-secondary space-y-0.5">
                <div v-if="violationDetails(n).vehicleName || violationDetails(n).licensePlate">
                  Vehicle:
                  <span class="font-semibold text-[var(--va-on-background-primary)]">
                    {{ violationDetails(n).vehicleName || violationDetails(n).licensePlate }}
                  </span>
                  <span v-if="violationDetails(n).licensePlate"> ({{ violationDetails(n).licensePlate }})</span>
                </div>
                <div v-if="violationDetails(n).type">
                  Type:
                  <span class="font-semibold text-[var(--va-on-background-primary)]">{{
                    violationDetails(n).type
                  }}</span>
                  <span v-if="formatViolationMetric(violationDetails(n).type, violationDetails(n).value)">
                    ·
                    <span class="font-semibold text-[var(--va-on-background-primary)]">{{
                      formatViolationMetric(violationDetails(n).type, violationDetails(n).value)
                    }}</span>
                  </span>
                </div>
                <div v-if="violationDetails(n).deviceId">
                  Device: <span class="font-mono">{{ violationDetails(n).deviceId }}</span>
                </div>
                <div v-if="violationDetails(n).occurredAt">
                  Occurred: {{ new Date(violationDetails(n).occurredAt).toLocaleString() }}
                </div>
              </div>
              <div class="text-[11px] text-secondary mt-1">
                {{ new Date(n.createdAt).toLocaleString() }}
              </div>
            </div>
            <VaButton v-if="!n.readAt" preset="secondary" size="small" @click.stop="handleMarkRead(n.id)"
              >Mark read</VaButton
            >
          </div>
        </div>
      </div>
    </VaModal>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { useColors } from 'vuestic-ui'
import VuesticLogo from '@/components/VuesticLogo.vue'
import ClientPortalSidebar from '@/components/sidebar/ClientPortalSidebar.vue'
import { useContractorAuthStore } from '@/stores/contractorAuthStore'
import {
  clearAllNotifications,
  clearNotificationsByPredicate,
  clearVehicleViolationNotifications,
  listPortalNotifications,
  markAllRead,
  markRead,
  portalUnreadCount,
  type ClientPortalNotification,
} from '@/services/clientPortalNotifications'
import { startClientViolationsRealtime, stopClientViolationsRealtime } from '@/services/clientPortalViolationsRealtime'

const contractorAuth = useContractorAuthStore()
const { clientLogoUrl, currentContractor: contractor } = storeToRefs(contractorAuth)
const route = useRoute()
const router = useRouter()

const isLiveTracker = computed(() => route.name === 'contractor-live-tracker')
const vehicleFeaturesEnabled = computed(() => contractorAuth.vehicleFeaturesEnabled)
const { applyPreset } = useColors()

const CLIENT_THEME_KEY = 'client_portal_theme'
const SIDEBAR_MINIMIZED_KEY = 'client_portal_sidebar_minimized'

const sidebarMinimized = ref(
  typeof window !== 'undefined' && localStorage.getItem(SIDEBAR_MINIMIZED_KEY) === 'true',
)

watch(sidebarMinimized, (value) => {
  if (typeof window === 'undefined') return
  localStorage.setItem(SIDEBAR_MINIMIZED_KEY, String(value))
})

const notificationsOpen = ref(false)
const notifications = ref<ClientPortalNotification[]>([])
const unread = ref(0)
const activeViolationTab = ref<'all' | 'overspeed' | 'sport_mode' | 'soc_warning' | 'other'>('all')
let notificationsPoll: number | undefined

function refreshNotifications() {
  const includeVehicleViolations = vehicleFeaturesEnabled.value
  notifications.value = listPortalNotifications({ includeVehicleViolations }).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )
  unread.value = portalUnreadCount({ includeVehicleViolations })
}

function syncVehicleViolationNotifications() {
  if (vehicleFeaturesEnabled.value) {
    startClientViolationsRealtime()
    return
  }
  stopClientViolationsRealtime()
  clearVehicleViolationNotifications()
  refreshNotifications()
}

function normalizeViolationType(notification: ClientPortalNotification): string {
  const meta = (notification.meta || {}) as Record<string, any>
  const violation = (meta.violation || {}) as Record<string, any>
  const raw = String(violation.type || '')
    .toLowerCase()
    .trim()
  if (!raw) return 'other'
  if (raw.includes('overspeed') || raw === 'speed') return 'overspeed'
  if (raw.includes('sport')) return 'sport_mode'
  if (raw.includes('soc')) return 'soc_warning'
  return 'other'
}

const filteredNotifications = computed(() => {
  if (activeViolationTab.value === 'all') return notifications.value
  return notifications.value.filter(
    (n) => n.type === 'violation' && normalizeViolationType(n) === activeViolationTab.value,
  )
})

const violationTabs = computed(() => {
  const countBy = (tab: 'all' | 'overspeed' | 'sport_mode' | 'soc_warning' | 'other') => {
    if (tab === 'all') return notifications.value.length
    return notifications.value.filter((n) => n.type === 'violation' && normalizeViolationType(n) === tab).length
  }
  return [
    { id: 'all' as const, label: 'All', count: countBy('all') },
    { id: 'overspeed' as const, label: 'Overspeed', count: countBy('overspeed') },
    { id: 'sport_mode' as const, label: 'Sport mode', count: countBy('sport_mode') },
    { id: 'soc_warning' as const, label: 'SOC warning', count: countBy('soc_warning') },
    { id: 'other' as const, label: 'Other', count: countBy('other') },
  ]
})

function violationDetails(notification: ClientPortalNotification) {
  const meta = (notification.meta || {}) as Record<string, any>
  const violation = (meta.violation || {}) as Record<string, any>
  const vehicle = (meta.vehicle || {}) as Record<string, any>
  return {
    vehicleName: String(vehicle.name || ''),
    licensePlate: String(vehicle.licensePlate || vehicle.license_plate || violation.license_plate || ''),
    model: String(vehicle.model || ''),
    deviceId: String(violation.device_id || vehicle.deviceId || ''),
    type: String(violation.type || ''),
    value: violation.value,
    occurredAt: String(violation.occurred_at || ''),
  }
}

function formatViolationMetric(type: string, value: unknown): string {
  if (value === undefined || value === null || value === '') return ''
  const normalized = String(type || '').toLowerCase()
  const valueStr = String(value).trim().toLowerCase()
  if (normalized.includes('sport_mode') || normalized.includes('sport mode')) {
    if (value === 1 || valueStr === '1' || valueStr === 'true' || valueStr === 'yes' || valueStr === 'on') return 'Yes'
    if (value === 0 || valueStr === '0' || valueStr === 'false' || valueStr === 'no' || valueStr === 'off') return 'No'
  }
  const num = Number(value)
  if (Number.isFinite(num)) {
    if (normalized.includes('speed') || normalized.includes('overspeed')) return `${Math.round(num)} km/h`
    if (normalized.includes('soc')) return `${Math.round(num)}%`
    return String(num)
  }
  return String(value)
}

onMounted(async () => {
  contractorAuth.restoreFromStorage()
  if (contractorAuth.token) {
    await contractorAuth.fetchProfile().catch(() => {})
  }

  const saved = localStorage.getItem(CLIENT_THEME_KEY)
  if (saved === 'dark' || saved === 'light') {
    applyPreset(saved)
  }

  refreshNotifications()
  syncVehicleViolationNotifications()
  notificationsPoll = window.setInterval(refreshNotifications, 1500)
})

watch(vehicleFeaturesEnabled, () => {
  syncVehicleViolationNotifications()
})

onUnmounted(() => {
  if (notificationsPoll) window.clearInterval(notificationsPoll)
  stopClientViolationsRealtime()
})

function logout() {
  const wasImpersonating = contractorAuth.isImpersonating
  contractorAuth.logout()
  if (wasImpersonating) {
    router.push({ name: 'contractor-management' })
  } else {
    router.push({ name: 'contractor-login' })
  }
}

function exitImpersonation() {
  contractorAuth.exitImpersonation()
  router.push({ name: 'contractor-management' })
}

function goPreferences() {
  router.push({ name: 'contractor-preferences' })
}

function handleMarkAllRead() {
  markAllRead()
  refreshNotifications()
}

function handleMarkRead(id: string) {
  markRead(id)
  refreshNotifications()
}

function handleClearAll() {
  clearAllNotifications()
  refreshNotifications()
}

function handleClearCurrentTab() {
  if (activeViolationTab.value === 'all') {
    clearAllNotifications()
    refreshNotifications()
    return
  }
  clearNotificationsByPredicate((n) => n.type === 'violation' && normalizeViolationType(n) === activeViolationTab.value)
  refreshNotifications()
}

function openNotification(notification: ClientPortalNotification) {
  handleMarkRead(notification.id)

  if (notification.type === 'violation') {
    if (!vehicleFeaturesEnabled.value) return
    localStorage.setItem('client_portal_focus_violation_notification_id', notification.id)
    window.dispatchEvent(
      new CustomEvent('client-portal-focus-violation', {
        detail: { notificationId: notification.id },
      }),
    )
    router.push({ name: 'contractor-live-tracker' })
  }

  notificationsOpen.value = false
}
</script>

<style scoped>
.contractor-layout__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem;
  background: var(--va-background-primary);
  border-bottom: 1px solid var(--va-background-border);
}
.contractor-layout__header-start {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.contractor-layout__menu-toggle {
  flex-shrink: 0;
}
.contractor-main {
  min-height: calc(100vh - 56px);
  background: var(--va-background-primary);
  color: var(--va-on-background-primary);
}

/* Ensure the layout content container inherits theme background too */
:deep(.va-layout__content) {
  background: var(--va-background-primary);
  color: var(--va-on-background-primary);
}
.contractor-layout__logo {
  display: flex;
  align-items: center;
}
.contractor-layout__logo-image {
  height: 28px;
  width: auto;
  object-fit: contain;
}
.contractor-layout__title {
  font-weight: 600;
  font-size: 1rem;
}
.contractor-layout__actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.contractor-layout__icon-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.contractor-layout__icon-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid var(--va-background-border);
  background: var(--va-background-element);
  color: var(--va-secondary);
  cursor: pointer;
}
.contractor-layout__icon-button:hover {
  background: color-mix(in srgb, var(--va-background-element) 80%, var(--va-primary) 20%);
  color: var(--va-on-background-element);
}
.contractor-layout__icon-button :deep(.va-icon) {
  font-size: 18px;
}
.contractor-layout__icon-button--notifications {
  color: #6b7280;
}
.contractor-layout__badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  border-radius: 999px;
  background: #ef4444;
  color: #ffffff;
  font-size: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 2px var(--va-background-primary);
}
.contractor-layout__avatar-button {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
}
.contractor-layout__avatar {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  object-fit: cover;
}
.contractor-layout__company {
  font-size: 0.875rem;
  color: var(--va-secondary);
}

.impersonation-banner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1.5rem;
  background: linear-gradient(90deg, #fef3c7 0%, #fde68a 100%);
  border-bottom: 1px solid #f59e0b;
  font-size: 0.875rem;
}
.impersonation-banner__icon {
  color: #b45309;
}

.contractor-main {
  padding: 1.5rem;
  max-width: none;
  margin: 0;
  min-height: calc(100vh - 56px);
  background: var(--va-background-primary);
  color: var(--va-on-background-primary);
}
.contractor-main--no-padding {
  padding: 0;
}
</style>
